import React, { useState } from 'react';
import { Plus, Search, Edit, Trash2, Bus as BusIcon, Calendar, User, Key } from 'lucide-react';
import { Card, Button, Input, Select, Badge } from '../../components/ui/index.jsx';

import { Modal } from '../../components/ui/Modal';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';


const BusList = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedBus, setSelectedBus] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const { hasPermission } = useAuth();
    const canCreate = hasPermission('buses', 'create');
    const canUpdate = hasPermission('buses', 'update');
    const canDelete = hasPermission('buses', 'delete');
    const { success, error: toastError } = useToast();

    
    const { data: busesResponse, loading, refresh: refreshBuses } = useGet(`/buses?page=${currentPage}&search=${searchTerm}`);
    const { data: driversRaw } = useGet('/drivers?status=active&all=1');
    const drivers = Array.isArray(driversRaw) ? driversRaw : (driversRaw?.data || []);

    const buses = Array.isArray(busesResponse) ? busesResponse : (busesResponse?.data || []);
    const pagination = busesResponse?.meta || null;

    // Filtered data (Search is now handled server-side if query is used, but we keep some client-side fallback if needed)
    const filteredBuses = Array.isArray(buses) ? buses : [];

    const handleDelete = async (id) => {
        if (window.confirm('هل أنت متأكد من حذف هذه الحافلة؟')) {
            try {
                await api.delete(`/buses/${id}`);
                success('تم حذف الحافلة بنجاح');
                refreshBuses();
            } catch (err) { toastError(err.response?.data?.message || 'خطأ أثناء الحذف'); }
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl  text-slate-800">قائمة الحافلات</h2>
                    <p className="text-slate-400 text-[11px] leading-none">إدارة الأسطول المدرسي ومواعيد الصيانة</p>
                </div>
                {canCreate && (
                    <Button onClick={() => { setSelectedBus(null); setIsModalOpen(true); }}>
                        <Plus className="w-5 h-5" /> إضافة حافلة
                    </Button>
                )}
            </div>

            <Card className="p-4">
                <Input 
                    placeholder="بحث برقم اللوحة أو كود الحافلة..." 
                    icon={Search}
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1); // Reset to first page
                    }}
                />
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading ? (
                    Array(3).fill(0).map((_, i) => <div key={i} className="h-48 bg-slate-100 animate-pulse rounded-lg" />)
                ) : filteredBuses.length === 0 ? (

                    <div className="col-span-full py-12 text-center text-slate-400 ">لا توجد حافلات حالياً</div>
                ) : (
                    filteredBuses.map(bus => (
                        <Card key={bus.id} className="p-4 h-full flex flex-col group">
                            <div className="flex items-start justify-between mb-4">

                                <div className="w-12 h-12 bg-orange-50 text-primary rounded-lg flex items-center justify-center">
                                    <BusIcon className="w-6 h-6" />
                                </div>

                                <div className="flex gap-1">
                                    {canUpdate && (
                                        <button onClick={() => { setSelectedBus(bus); setIsModalOpen(true); }} className="p-2 text-slate-400 hover:text-primary transition-colors border border-slate-50 rounded-lg bg-slate-50/50"><Edit size={18} /></button>
                                    )}
                                    {canDelete && (
                                        <button onClick={() => handleDelete(bus.id)} className="p-2 text-slate-400 hover:text-red-500 transition-colors border border-slate-50 rounded-lg bg-slate-50/50"><Trash2 size={18} /></button>
                                    )}
                                </div>
                            </div>
                            
                            <h3 className="text-lg  text-slate-800 mb-1">{bus.matricule}</h3>
                            <p className="text-[10px]  text-primary uppercase tracking-widest mb-4 flex items-center gap-1.5 bg-orange-50 px-2 py-0.5 rounded-sm w-fit italic">
                                <Key size={10} /> {bus.code}
                            </p>


                                <div className="flex items-center justify-between text-xs py-2 border-b border-slate-50">
                                    <span className="text-slate-400  flex items-center gap-1.5"><BusIcon size={14} /> نوع الحافلة:</span>
                                    <span className="text-slate-800 ">{bus.bus_type || '--'}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs py-2 border-b border-slate-50">
                                    <span className="text-slate-400  flex items-center gap-1.5"><Calendar size={14} /> المرجع:</span>
                                    <span className="text-slate-800 ">{bus.reference || '--'}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs py-2 border-b border-slate-50">
                                    <span className="text-slate-400  flex items-center gap-1.5"><Calendar size={14} /> تاريخ التفعيل:</span>
                                    <span className="text-slate-800 ">{new Date(bus.mise_en_service).toLocaleDateString('ar-MA')}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs py-2">
                                    <span className="text-slate-400  flex items-center gap-1.5"><User size={14} /> السائق:</span>
                                    <span className="text-slate-800 ">{bus.driver?.full_name || '--'}</span>
                                </div>
                        </Card>
                    ))
                )}
            </div>

            {/* Pagination Controls */}
            {pagination && pagination.last_page > 1 && (
                <Card className="px-6 py-4 bg-slate-50/30 border-t border-slate-100 flex items-center justify-between">
                    <div className="text-xs text-slate-500 font-normal">
                        عرض <span className="font-medium text-slate-700">{pagination.from}</span> إلى <span className="font-medium text-slate-700">{pagination.to}</span> من أصل <span className="font-medium text-slate-700">{pagination.total}</span> حافلة
                    </div>
                    <div className="flex items-center gap-2">
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="h-8 px-3 text-xs"
                        >
                            السابق
                        </Button>
                        <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                                let pageNum = i + 1;
                                if (pagination.last_page > 5 && currentPage > 3) {
                                    pageNum = currentPage - 3 + i + 1;
                                    if (pageNum > pagination.last_page) pageNum = pagination.last_page - (4 - i);
                                }
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`w-8 h-8 rounded-lg text-xs transition-all ${
                                            currentPage === pageNum 
                                            ? 'bg-primary text-white shadow-sm font-medium' 
                                            : 'text-slate-500 hover:bg-slate-100'
                                        }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.last_page))}
                            disabled={currentPage === pagination.last_page}
                            className="h-8 px-3 text-xs"
                        >
                            التالي
                        </Button>
                    </div>
                </Card>
            )}

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedBus ? 'تعديل بيانات الحافلة' : 'إضافة حافلة جديدة'}>
                <BusForm 
                    bus={selectedBus} 
                    drivers={drivers}
                    onSuccess={() => { setIsModalOpen(false); refreshBuses(); }} 
                />
            </Modal>
        </div>
    );
};

const BusForm = ({ bus, drivers, onSuccess }) => {
    const [formData, setFormData] = useState({
        code: bus?.code || '',
        matricule: bus?.matricule || '',
        bus_type: bus?.bus_type || '',
        reference: bus?.reference || '',
        mise_en_service: bus?.mise_en_service || '',
        driver_id: bus?.driver_id || ''
    });
    const [loading, setLoading] = useState(false);
    const { success, error: toastError } = useToast();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (bus) {
                await api.put(`/buses/${bus.id}`, formData);
                success('تم تحديث بيانات الحافلة بنجاح');
            } else {
                await api.post('/buses', formData);
                success('تم إضافة الحافلة بنجاح');
            }
            onSuccess();
        } catch (err) { toastError('خطأ أثناء الحفظ'); }
        finally { setLoading(false); }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="كود الحافلة" value={formData.code} onChange={e => setFormData({...formData, code: e.target.value})} required/>
                <Input label="رقم اللوحة (Matricule)" value={formData.matricule} onChange={e => setFormData({...formData, matricule: e.target.value})} required/>
                <Input label="تاريخ التشغيل" type="date" value={formData.mise_en_service} onChange={e => setFormData({...formData, mise_en_service: e.target.value})} required/>
                <Input label="نوعية الحافلة" value={formData.bus_type} onChange={e => setFormData({...formData, bus_type: e.target.value})} />
                <Input label="المرجع (الاتفاقيات والملاحق)" value={formData.reference} onChange={e => setFormData({...formData, reference: e.target.value})} />
                <Select 
                    label="السائق الموكل" 
                    options={[
                        { value: '', label: 'اختر السائق' },
                        ...(bus?.driver && !drivers.some(d => d.id === bus.driver.id) ? [{ value: bus.driver.id, label: `${bus.driver.full_name} (غير نشط)` }] : []),
                        ...(drivers?.map(d => ({ value: d.id, label: d.full_name })) || [])
                    ]}
                    value={formData.driver_id} 
                    onChange={e => setFormData({...formData, driver_id: e.target.value})} 
                />
            </div>
            <Button type="submit" isLoading={loading} className="w-full">حفظ الحافلة</Button>
        </form>
    );
};

export default BusList;
